import React, {FC, useState, useEffect} from "react"
import {Row} from "react-bootstrap"
import QRCode from "qrcode.react"
import styled, {css} from "styled-components"
import moment from "moment"

import {Body, H1, H3, H4, H5, Label} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {Email, Firstname, Lastname} from "@irati-web/fields/user"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {useAddIndividual, useUpdateEnquiry, useIgnoreIndividualCheck} from "@irati/signup/hooks"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import Middlename from "@irati-web/fields/user/Middlename"
import {hangPromise} from "@irati/utils/promise"
import {SignUp, SignUpOutcomeIndicator, SignUpStage} from "@irati/types"
import Link from "@irati/ui/src/Link"
import ToastAlert from "@irati/ui/src/ToastAlert"
import ga4Tracking from "@irati-web/tracking"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import {UseQueriesResult} from "@irati/utils/services/apollo"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

import Spacing from "@irati/ui/src/Spacing"
import Button from "@irati/ui/src/Button"
import Loading from "@irati/ui/src/Loading"
import Spinner from "@irati/ui/src/Loading/Spinner"
import ReviewingCustomer from "../../components/SignUp/Cards/ReviewingCustomer"
import IndividualVerified from "../../components/SignUp/Cards/IndividualVerified"
import NorthrowLink from "../../components/SignUp/NorthrowLink"
import TextWithTick from "../../components/SignUp/TextWithTick"
import SaveAndProgressFooter from "../../components/SignUp/SaveAndProgressFooter"
import SaveAndProgressMobile from "../../components/SignUp/SaveAndProgressMobile"
import {StyledCard, StyledFlex, StyledIcon, MobileFlex, DesktopOnlyWrapper} from "../../components/SignUp/styled"
import EditDetailsModal, {SBSUser} from "../../components/SignUp/EditDetailsModal"

const Wrapper = styled(Flex)`
  ${({theme}) => css`
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    max-width: 1030px;
    ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
    align-items: unset;
  `}
  `}
`

const CopyLink = styled.button`
  display: flex;
  cursor: pointer;
  border: 1px solid ${({theme}) => theme.colors.greys.grey20};
  background-color: ${({theme}) => theme.colors.greys.white};
  border-radius: 20px;
  padding: 8px 16px;
  margin-bottom: ${({theme}) => theme.spacing.four};
`

const CopyText = styled(Body)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 235px;
`

const Container = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.maxWidthMedia.phoneLandscape`
      flex-direction: column;
   `}
  `}
`

interface Props {
  signUp: UseQueriesResult<SignUp>
  onKYCCompleted: () => void
}

const KnowYourCustomer: FC<Props> = ({signUp, onKYCCompleted}) => {
  const link = signUp?.data?.individualChecks?.find(
    (individualCheck) =>
      individualCheck?.link?.url &&
      !individualCheck?.ignored &&
      (!individualCheck?.outcome || individualCheck?.outcome?.indicator === SignUpOutcomeIndicator.CheckIndicatorGreen)
  )?.link?.url

  const individualCheckNonIgnored = signUp?.data?.individualChecks?.find((individualCheck) => !individualCheck?.ignored)

  const [firstName, setFirstName] = useState(signUp?.data.enquiry.firstName)
  const [middleName, setMiddleName] = useState(signUp?.data.enquiry.middleName)
  const [lastName, setLastName] = useState(signUp?.data.enquiry.lastName)
  const [emailAddress, setEmailAddress] = useState(signUp?.data.enquiry.emailAddress)
  const [showTips, setShowTips] = useState(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [success, setShowSuccess] = useState(false)
  const [showReviewingCustomer, setShowReviewingCustomer] = useState(false)
  const [detailsConfirmed, setDetailsConfirmed] = useState(!!link)
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false)
  const [error, setError] = useState(false)
  const theme = useTheme()
  const {formatMessage} = useIntl()

  const uuid = signUp?.data?.uuid

  const [addIndividual, addIndividualApollo] = useAddIndividual({
    variables: {uuid, individual: {firstName, lastName, emailAddress, middleName: middleName || undefined}}
  })
  const [updateEnquiry] = useUpdateEnquiry({
    variables: {
      uuid,
      enquiry: {
        ...signUp.data.enquiry,
        firstName,
        lastName,
        middleName: middleName || undefined
      }
    }
  })
  const [ignoreIndividualCheck, useIgnoreIndividualCheckApollo] = useIgnoreIndividualCheck({
    variables: {signupId: uuid, checkId: individualCheckNonIgnored?.uuid}
  })

  useEffect(() => {
    ga4Tracking.trackEventWithStore("kyc_viewed", "sbs_params", {step: "4", email: emailAddress, page: "kyc_page"})

    if (link) {
      signUp.startPolling(10000)
    }
  }, [])

  useEffect(() => {
    if (addIndividualApollo?.data) {
      signUp.stopPolling()
      updateEnquiry()
      setDetailsConfirmed(true)
      signUp.startPolling(10000)
    }
  }, [addIndividualApollo?.data])

  useEffect(() => {
    if (useIgnoreIndividualCheckApollo?.data) {
      signUp.refetch()
      setShowReviewingCustomer(false)
      setDetailsConfirmed(false)
    }
  }, [useIgnoreIndividualCheckApollo?.data])

  useEffect(() => {
    if (addIndividualApollo?.error) {
      setError(true)
      setLoad(null)
    }
  }, [addIndividualApollo?.error])

  useEffect(() => {
    const amberOrRedAlert =
      individualCheckNonIgnored?.outcome?.indicator &&
      individualCheckNonIgnored?.outcome?.indicator !== SignUpOutcomeIndicator.CheckIndicatorGreen

    if (signUp.data?.stage !== SignUpStage.SignUpStageIndividual) {
      signUp.stopPolling()
      setShowSuccess(true)
    } else if (link) {
      setLoad(null)
    } else if (amberOrRedAlert) {
      signUp.stopPolling()
      setShowReviewingCustomer(true)
    }
  }, [signUp.data])

  useEffect(() => {
    setError(false)
  }, [firstName, lastName, middleName, emailAddress])

  const handleConfirm = () => {
    const detailsUpdated =
      signUp.firstName !== firstName &&
      signUp.lastName !== lastName &&
      signUp.emailAddress !== emailAddress &&
      signUp.middleName !== middleName

    ga4Tracking.trackEventWithStore("kyc_started", "sbs_params", {
      step: "4.1",
      kyc_start_time: moment().format("DD/MM/YYYY, h:mm:ss A"),
      first_name: firstName,
      last_name: lastName,
      details_updated: detailsUpdated ? "Y" : "N",
      email: emailAddress,
      page: "kyc_page"
    })
    setLoad(hangPromise())
    addIndividual()
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      triggerSuccessToast(formatMessage({id: "platform.selfSignUp.kyc.northrowCopy"}))
    })
  }

  const handleTryAgain = () => {
    ignoreIndividualCheck()
  }

  const handleEditDetails = (user: SBSUser) => {
    setFirstName(user?.firstName)
    setMiddleName(user?.middleName)
    setLastName(user?.lastName)
    setEmailAddress(user?.emailAddress)
    setShowEditDetailsModal(false)
  }

  if (success) {
    ga4Tracking.trackEventWithStore("kyc_submitted ", "sbs_params", {step: "4.2", email: emailAddress, page: "kyc_page"})
    return (
      <>
        <IndividualVerified />
        <SaveAndProgressFooter onConfirm={onKYCCompleted} confirmText={formatMessage({id: "platform.selfSignUp.kyc.continue"})} />
        <SaveAndProgressMobile onConfirm={onKYCCompleted} confirmText={formatMessage({id: "platform.selfSignUp.kyc.continue"})} />
      </>
    )
  }

  if (showReviewingCustomer) {
    return <ReviewingCustomer onClick={handleTryAgain} underReview={signUp?.data?.individualChecks?.length >= 3} />
  }

  const title = detailsConfirmed ? "platform.selfSignUp.kyc.detailsConfirmedTitle" : "platform.selfSignUp.kyc.title"

  return (
    <>
      <ToastAlert />
      <Wrapper direction="column">
        <StyledFlex gap={1}>
          <StyledCard>
            <H1 color={theme.colors.primary2} marginBottom={2}>
              <FormattedMessage id={title} />
            </H1>
            {error && (
              <Alert
                type={AlertTypes.error}
                title={formatMessage({id: "platform.selfSignUp.kyc.generalError"})}
                onHide={() => setError(false)}
              />
            )}
            {detailsConfirmed ? (
              <div>
                <Container gap={1}>
                  <Flex minWidth="200px" width="50%" alignItems="center" gap={1}>
                    <H5 color={theme.colors.primary2}>
                      <FormattedMessage id="platform.fields.firstName.label" />:
                    </H5>
                    <Label color={theme.colors.primary2}>{firstName}</Label>
                  </Flex>
                  <Flex minWidth="200px" width="50%" alignItems="center" gap={1}>
                    <H5 color={theme.colors.primary2}>
                      <FormattedMessage id="platform.fields.middleName.label" />:
                    </H5>
                    <Label color={theme.colors.primary2}>{middleName}</Label>
                  </Flex>
                </Container>
                <Flex alignItems="center" gap={1} marginTop={1}>
                  <H5 color={theme.colors.primary2}>
                    <FormattedMessage id="platform.fields.lastName.label" />:
                  </H5>
                  <Label color={theme.colors.primary2}>{lastName}</Label>
                </Flex>
                <Flex alignItems="center" gap={1} marginTop={1}>
                  <H5 color={theme.colors.primary2}>
                    <FormattedMessage id="platform.fields.email.label" />:
                  </H5>
                  <Label color={theme.colors.primary2}>{emailAddress}</Label>
                </Flex>
                <Loading load={load} Loader={() => <Spinner />}>
                  <Flex marginTop={4} marginBottom={2}>
                    <Label color={theme.colors.primary2}>
                      <FormattedMessage id="platform.selfSignUp.kyc.qrCode" />{" "}
                      <Link href={formatMessage({id: "platform.selfSignUp.footer.privacyLink"})} target="_blank">
                        <FormattedMessage id="platform.selfSignUp.footer.privacy" />
                      </Link>
                      .
                    </Label>
                  </Flex>
                  <Flex alignItems="center" justifyContent="center" direction="column">
                    <QRCode value={link} size={220} />
                    <Body small marginTop={2} marginBottom={1} color={theme.colors.primary2}>
                      <FormattedMessage id="platform.selfSignUp.kyc.completeVerification" />
                    </Body>
                    <CopyLink onClick={handleCopyLink}>
                      <CopyText small color={theme.colors.primary2}>
                        {link}
                      </CopyText>
                      <Icon icon={IconTypes.files} size={24} color={theme.colors.primary1} />
                    </CopyLink>
                  </Flex>
                </Loading>
              </div>
            ) : (
              <Loading load={load} Loader={() => <Spinner />}>
                <Alert type={AlertTypes.warning} title={formatMessage({id: "platform.selfSignUp.kyc.tip"})} nonDismissable />
                <Body color={theme.colors.primary2} marginBottom={2}>
                  <FormattedMessage id="platform.selfSignUp.kyc.subTitle" />
                </Body>
                <Row>
                  <Firstname value={firstName} onChange={setFirstName} disabled />
                </Row>
                <Row>
                  <Middlename value={middleName} onChange={setMiddleName} disabled />
                </Row>
                <Spacing marginBottom={1} />
                <Row>
                  <Lastname value={lastName} onChange={setLastName} disabled />
                </Row>
                <Row>
                  <Email value={emailAddress} onChange={setEmailAddress} disabled />
                </Row>
                <Button onClick={() => setShowEditDetailsModal(true)} marginTop={1} marginBottom={3}>
                  <FormattedMessage id="platform.selfSignUp.kyc.editDetails" />
                </Button>
              </Loading>
            )}
            <SaveAndProgressMobile
              onConfirm={detailsConfirmed ? undefined : handleConfirm}
              confirmText={formatMessage({id: "platform.selfSignUp.kyc.confirm"})}
            />
            <Flex alignItems="center">
              <Label color={theme.colors.primary2} marginRight={0.5}>
                <FormattedMessage id="platform.selfSignUp.kyc.verifyIdentity" />
              </Label>
              <Tooltip tooltipChildren={formatMessage({id: "platform.selfSignUp.kyc.accountSecurity"})} placement={OverlayPlacement.Top}>
                <StyledIcon icon={IconTypes.circleAlertFill} size={20} />
              </Tooltip>
            </Flex>
            <DesktopOnlyWrapper>
              <Flex justifyContent="center" marginTop={2} alignItems="center" gap={0.5}>
                <Icon color={theme.colors.greys.grey50} size={10} icon={IconTypes.padlock} />
                <NorthrowLink />
              </Flex>
            </DesktopOnlyWrapper>
          </StyledCard>
          <StyledCard>
            <MobileFlex direction="column">
              <Flex alignItems="center" gap={1} minWidth="408px">
                <img src={formatMessage({id: "platform.selfSignUp.kyc.img1"})} height={48} width={48} alt="verifyDetails" />
                <H3 color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.kyc.step1" />:
                </H3>
                <Body color={theme.colors.primary2} large>
                  <FormattedMessage id="platform.selfSignUp.kyc.verifyDetails" />
                </Body>
              </Flex>
              <Label color={theme.colors.primary2}>
                <FormattedMessage id="platform.selfSignUp.kyc.confirmDetails" />
              </Label>
            </MobileFlex>
            <MobileFlex direction="column">
              <Flex alignItems="center" gap={1} marginTop={2}>
                <img src={formatMessage({id: "platform.selfSignUp.kyc.img2"})} height={48} width={48} alt="submitYourID" />
                <H3 color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.kyc.step2" />:
                </H3>
                <Body color={theme.colors.primary2} large>
                  <FormattedMessage id="platform.selfSignUp.kyc.submitId" />
                </Body>
              </Flex>
              <Label color={theme.colors.primary2}>
                <FormattedMessage id="platform.selfSignUp.kyc.photoId" />
              </Label>
            </MobileFlex>
            <MobileFlex direction="column">
              <Flex alignItems="center" gap={1} marginTop={2}>
                <img src={formatMessage({id: "platform.selfSignUp.kyc.img3"})} height={48} width={48} alt="livelinessCheck" />
                <H3 color={theme.colors.primary2}>
                  <FormattedMessage id="platform.selfSignUp.kyc.step3" />:
                </H3>
                <Body color={theme.colors.primary2} large>
                  <FormattedMessage id="platform.selfSignUp.kyc.liveliness" />
                </Body>
              </Flex>
              <Label color={theme.colors.primary2}>
                <FormattedMessage id="platform.selfSignUp.kyc.livelinessDetails" />
              </Label>
            </MobileFlex>
            <Flex gap={1} marginTop={3} onClick={() => setShowTips(!showTips)}>
              <H4 color={theme.colors.primary2}>
                <FormattedMessage id="platform.selfSignUp.kyc.tips" />
              </H4>
              <Icon icon={showTips ? IconTypes.chevronUp : IconTypes.chevronDown} size={16} />
            </Flex>
            {showTips && (
              <MobileFlex direction="column" gap={1} marginTop={2}>
                <TextWithTick text={formatMessage({id: "platform.selfSignUp.kyc.tip1"})} />
                <TextWithTick text={formatMessage({id: "platform.selfSignUp.kyc.tip2"})} />
                <TextWithTick text={formatMessage({id: "platform.selfSignUp.kyc.tip3"})} />
                <TextWithTick text={formatMessage({id: "platform.selfSignUp.kyc.tip4"})} />
                <TextWithTick text={formatMessage({id: "platform.selfSignUp.kyc.tip5"})} />
              </MobileFlex>
            )}
          </StyledCard>
        </StyledFlex>
        <SaveAndProgressFooter
          onConfirm={detailsConfirmed ? undefined : handleConfirm}
          confirmText={formatMessage({id: "platform.selfSignUp.kyc.confirm"})}
        />
      </Wrapper>
      {showEditDetailsModal && (
        <EditDetailsModal
          user={{firstName, middleName, lastName, emailAddress}}
          show
          onHide={() => setShowEditDetailsModal(false)}
          onConfirm={handleEditDetails}
          hideCompanyName
        />
      )}
    </>
  )
}

export default KnowYourCustomer
